// Nutrition calculation utilities

/**
 * Convert steps to calories burned
 * Formula: steps * 0.04 * (bodyWeight / 70)
 * Average ~0.04 kcal per step for 70kg person
 */
export const stepsToCalories = (steps, bodyWeight = 70) => {
  if (!steps || steps <= 0) return 0;
  const caloriesPerStep = 0.04 * (bodyWeight / 70);
  return Math.round(steps * caloriesPerStep);
};

/**
 * Calculate total macros from food entries
 */
export const calculateTotalMacros = (entries) => {
  return entries.reduce(
    (acc, entry) => ({
      calories: acc.calories + (entry.calories || 0),
      protein: acc.protein + (entry.protein || 0),
      carbs: acc.carbs + (entry.carbs || 0),
      fat: acc.fat + (entry.fat || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );
};

/**
 * Calculate percentage of goal achieved
 */
export const calculatePercentage = (current, goal) => {
  if (!goal || goal === 0) return 0;
  return Math.min(Math.round((current / goal) * 100), 100);
};

/**
 * Calculate BMR (Basal Metabolic Rate) using Mifflin-St Jeor equation
 */
export const calculateBMR = (weight, height, age, gender) => {
  if (!weight || !height || !age) return 0;
  
  // Mifflin-St Jeor equation
  const baseBMR = (10 * weight) + (6.25 * height) - (5 * age);
  return Math.round(gender === 'female' ? baseBMR - 161 : baseBMR + 5);
};

/**
 * Calculate TDEE (Total Daily Energy Expenditure)
 */
export const calculateTDEE = (bmr, activityLevel = 1.2) => {
  return Math.round(bmr * activityLevel);
};

/**
 * Get activity level multiplier description
 */
export const ACTIVITY_LEVELS = {
  1.2: 'Sedentary (little or no exercise)',
  1.375: 'Lightly active (1-3 days/week)',
  1.55: 'Moderately active (3-5 days/week)',
  1.725: 'Very active (6-7 days/week)',
  1.9: 'Extra active (very hard exercise)',
};

/**
 * Calculate daily calorie target based on weight goal
 * For 'lose', uses progressive adjustment based on week number
 * @param {number} tdee - base TDEE (BMR × activity)
 * @param {string} weightGoal - 'lose' or 'maintain'
 * @param {number} calorieAdjustment - from calculateGoalAdjustment() (0 or negative)
 */
export const calculateDailyTarget = (tdee, weightGoal, calorieAdjustment = 0) => {
  if (weightGoal === 'lose') {
    return Math.round(tdee + calorieAdjustment);
  }
  return tdee; // maintain
};

/**
 * Format date to YYYY-MM-DD using local timezone
 */
export const formatDate = (date) => {
  if (typeof date === 'string') return date.split('T')[0];
  // Use local timezone instead of UTC
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get today's date in YYYY-MM-DD format using local timezone
 */
export const getToday = () => formatDate(new Date());

/**
 * Add days to a date string
 */
export const addDays = (dateString, days) => {
  const date = new Date(dateString);
  date.setDate(date.getDate() + days);
  return formatDate(date);
};
